import React, { useEffect, useMemo, useState } from 'react';
import { Col, Form, Row, Select, Skeleton, Table } from 'antd';
import { PieChartOutlined } from '@ant-design/icons';
import { Bar, BarChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Cell, Legend } from 'recharts';
import PersianDatePicker from '../../components/PersianDatePicker';
import ReportPageLayout from './ReportPageLayout';
import ReportFiltersCard from './ReportFiltersCard';
import { reportsApi } from '../../utils/api';
import { getLast30DaysRange } from '../../utils/dateUtils';
import { extractPagination, extractRows, getDateRangePickerValue, parseDateRangeChange, toNumber } from './reportUtils';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import ChartEmptyState from './ChartEmptyState';
import { formatReportNumberFa, formatReportMoneyFa, formatReportTableAmount, REPORT_CHART_COLORS, REPORT_CHART_PALETTE } from './chartFormatters';
import { AmountBarTooltip, PieCategoryTooltip } from './ReportChartTooltips';

const ExpenseReport = () => {
  const range = getLast30DaysRange('jYYYY-MM-DD', 'jalali');
  const [filters, setFilters] = useState({
    startDate: range.startDate,
    endDate: range.endDate,
    category_id: 'all',
    project_id: 'all',
    employee_id: 'all',
    page: 1,
    per_page: 15,
  });
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });
  const [categoryRows, setCategoryRows] = useState([]);
  const [apiFilters, setApiFilters] = useState({});

  const load = async (nextFilters) => {
    if (!nextFilters.startDate || !nextFilters.endDate) return;
    setLoading(true);
    try {
      const [res, categoryRes] = await Promise.all([
        reportsApi.getExpenseReport(nextFilters),
        reportsApi.getExpenseCategoryReport(nextFilters),
      ]);
      setRows(extractRows(res));
      setPagination(extractPagination(res));
      setCategoryRows(extractRows(categoryRes));
      setApiFilters(res?.filters || categoryRes?.filters || {});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => load(filters), 350);
    return () => clearTimeout(timer);
  }, [filters]);

  const expenseChartData = useMemo(
    () => rows.map((r, i) => ({ label: r?.date || String(i + 1), amount: toNumber(r?.amount || r?.total || r?.expense_amount) })),
    [rows],
  );
  const categoryChartData = useMemo(
    () => categoryRows.map((r) => ({ label: r?.category_name || r?.name || 'بدون عنوان', value: toNumber(r?.amount || r?.total || r?.expense_total) })),
    [categoryRows],
  );

  return (
    <ReportPageLayout
      headerIcon={<PieChartOutlined />}
      title="گزارش هزینه‌ها"
      subtitle="تحلیل هزینه‌ها و دسته‌بندی هزینه"
    >
      <ReportFiltersCard>
        <Form layout="vertical" className="custom-font">
          <Row gutter={[16, 12]}>
            <Col xs={24} lg={10}>
              <Form.Item label="بازه تاریخ" className="mb-0">
                <PersianDatePicker
                  range
                  placeholder="انتخاب بازه از تاریخ تا تاریخ"
                  value={getDateRangePickerValue(filters.startDate, filters.endDate)}
                  onChange={(dates) => setFilters((p) => ({ ...p, ...parseDateRangeChange(dates), page: 1 }))}
                  style={{ minHeight: 36 }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8} lg={5}>
              <Form.Item label="دسته‌بندی" className="mb-0">
                <Select
                  value={filters.category_id}
                  onChange={(v) => setFilters((p) => ({ ...p, category_id: v, page: 1 }))}
                  options={[{ value: 'all', label: 'همه' }, ...(apiFilters?.categories || []).map((i) => ({ value: i.id, label: i.name }))]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8} lg={5}>
              <Form.Item label="پروژه" className="mb-0">
                <Select
                  value={filters.project_id}
                  onChange={(v) => setFilters((p) => ({ ...p, project_id: v, page: 1 }))}
                  options={[{ value: 'all', label: 'همه' }, ...(apiFilters?.projects || []).map((i) => ({ value: i.id, label: i.project_name || i.name }))]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8} lg={4}>
              <Form.Item label="کارمند" className="mb-0">
                <Select
                  value={filters.employee_id}
                  onChange={(v) => setFilters((p) => ({ ...p, employee_id: v, page: 1 }))}
                  options={[{ value: 'all', label: 'همه' }, ...(apiFilters?.employees || []).map((i) => ({ value: i.id, label: i.name }))]}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </ReportFiltersCard>

      <Row gutter={[16, 16]} className="mb-5">
        <Col xs={24} lg={12}>
          <div className="h-60 border border-slate-200/80 dark:border-slate-600 rounded-xl p-2 bg-white dark:bg-slate-900/50" dir="ltr">
            {loading && !expenseChartData.length ? (
              <Skeleton active paragraph={{ rows: 7 }} />
            ) : expenseChartData.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={expenseChartData} margin={{ top: 6, right: 10, left: 0, bottom: 4 }}>
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} tickMargin={6} interval="preserveStartEnd" />
                  <YAxis
                    width={88}
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => formatReportMoneyFa(v)}
                    tickMargin={4}
                  />
                  <Tooltip content={<AmountBarTooltip />} />
                  <Bar dataKey="amount" name="مبلغ" fill={REPORT_CHART_COLORS.sky} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmptyState minHeight={260} />
            )}
          </div>
        </Col>
        <Col xs={24} lg={12}>
          <div className="h-60 border border-slate-200/80 dark:border-slate-600 rounded-xl p-2 bg-white dark:bg-slate-900/50" dir="ltr">
            {loading && !categoryChartData.length ? (
              <Skeleton active paragraph={{ rows: 7 }} />
            ) : categoryChartData.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={categoryChartData} dataKey="value" nameKey="label" outerRadius={88}>
                    {categoryChartData.map((_, i) => (
                      <Cell key={i} fill={REPORT_CHART_PALETTE[i % REPORT_CHART_PALETTE.length]} />
                    ))}
                  </Pie>
                  <Legend wrapperStyle={{ direction: 'rtl', fontFamily: 'inherit' }} />
                  <Tooltip content={<PieCategoryTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmptyState minHeight={260} />
            )}
          </div>
        </Col>
      </Row>

      {loading && !rows.length ? (
        <TableSkeleton rows={6} columns={6} />
      ) : (
        <Table
          rowKey={(r, i) => r.id || i}
          dataSource={rows}
          loading={loading}
          scroll={{ x: 1100 }}
          className="custom-font"
          pagination={{
            ...pagination,
            onChange: (page, pageSize) => {
              const next = { ...filters, page, per_page: pageSize };
              setFilters(next);
            },
          }}
          columns={[
            { title: 'تاریخ', dataIndex: 'date' },
            { title: 'عنوان', dataIndex: 'title' },
            { title: 'دسته‌بندی', render: (_, r) => r?.category_name || r?.category || '-' },
            { title: 'مبلغ', render: (_, r) => formatReportTableAmount(r?.amount ?? r?.expense_amount) },
            { title: 'کارمند', render: (_, r) => r?.employee_name || '-' },
            { title: 'پروژه', render: (_, r) => r?.project_name || '-' },
          ]}
        />
      )}
    </ReportPageLayout>
  );
};

export default ExpenseReport;
